import { AuthSession } from "@aws-amplify/core";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { toast } from "@indigo-components";
import { Amplify } from "aws-amplify";
import { AuthUser, getCurrentUser } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import AWS from "aws-sdk";
import { getDefaultStore } from "jotai";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { analytics } from "~/analytics";
import { impersonationAtom } from "~/atoms/impersonation";
import { fetchAuthSessionWithRetry, isThrottleError } from "~/auth/authToken";
import {
  clearCognitoLocalStorageKeys,
  hasAdminSessionBackup,
  restoreAdminSession,
} from "~/auth/sessionBackup";
import { setSessionGraceActive } from "~/auth/sessionGrace";
import { STALE_IDENTITY_RECOVERED_EVENT } from "~/auth/singleFlightCredentialsProvider";
import { getCurrentEnv } from "~/common/utils";
import { errorReporter } from "~/error-handling";
import { toError } from "~/error-handling/errorUtils";
import {
  armImpersonationFailureForTest,
  consumeImpersonationError,
} from "~/hooks/useImpersonation";
import { AuthUserGroup } from "~/types/auth";

// Derive SignOut type from useAuthenticator hook return type
// (avoids importing from internal dist paths)
type SignOut = ReturnType<typeof useAuthenticator>["signOut"];

interface AuthSessionContextType {
  // Auth session data
  authSession: AuthSession | null;
  userGroup: AuthUserGroup | null;
  isLoading: boolean;

  // AWS SDK clients
  s3: AWS.S3 | null;
  lambda: AWS.Lambda | null;
  cognitoIdp: AWS.CognitoIdentityServiceProvider | null;

  // Auth user (from Authenticator)
  authUser: AuthUser | null;
  signOut?: SignOut;

  // True when the session expired but we're keeping the user on their current
  // page (with a dismissable banner) instead of hard-redirecting to /login.
  // Entered for any established staff session — see configureAuthSession's
  // invalid-session path.
  sessionExpired: boolean;

  // Errors
  error: Error | null;
}

const AuthSessionContext = createContext<AuthSessionContextType | null>(null);

// Time between auth health checks
const HEALTH_CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
// With a 10-minute interval, refreshing with 15 minutes left is a safe buffer.
const HEALTH_CHECK_BUFFER_MS = 15 * 60 * 1000; // 15 minutes

// Enable transfer acceleration for better upload/download performance
const USE_TRANSFER_ACCELERATION = true;

// After Cognito throttles credential refresh (TooManyRequestsException), pause
// forced refreshes for this long to let the rate limit recover instead of
// re-hitting it. Shorter than the health-check interval so recovery is quick.
const THROTTLE_COOLDOWN_MS = 30 * 1000; // 30 seconds

// The 10-minute health-check interval fires in every open tab (including hidden
// ones) and, after a laptop sleep, all of them resume at roughly the same
// instant — a synchronized multi-tab burst of forced refreshes that hammers the
// Identity Pool. Spread each tab's refresh over a random window so the calls
// don't land together. Only delays a background timer, so latency is a non-issue.
const HEALTH_CHECK_JITTER_MS = 5 * 1000; // up to 5 seconds

// Cognito Identity throttling detection lives in ~/auth/authToken (isThrottleError)
// so the provider and the bare-fetch call sites share one definition. A throttle
// must NOT clear the session or redirect; it should just back off.

const isInvalidAuthSessionError = (error: Error): boolean => {
  const name = error.name.toLowerCase();
  const message = error.message.toLowerCase();

  return (
    name.includes("notauthorized") ||
    name.includes("nosigneduser") ||
    message.includes("invalid login token") ||
    message.includes("token expired") ||
    message.includes("no current user") ||
    message.includes("nosigneduser") ||
    message.includes("no signed user")
  );
};

const getLoginRedirectUrl = (): string => {
  if (window.location.pathname === "/login") {
    return "/login";
  }

  const returnUrl = encodeURIComponent(
    window.location.pathname + window.location.search + window.location.hash,
  );

  return `/login?returnUrl=${returnUrl}`;
};

export function getAuthUserGroup(session: AuthSession): AuthUserGroup {
  const groups = session.tokens?.accessToken.payload["cognito:groups"];
  if (!groups || !Array.isArray(groups)) {
    return AuthUserGroup.UNAUTHORIZED;
  }

  const hasGroup = (groupName: string) =>
    groups.some(
      (group): group is string =>
        typeof group === "string" &&
        group.toLowerCase() === groupName.toLowerCase(),
    );

  if (hasGroup("Admin")) {
    return AuthUserGroup.ADMIN;
  }

  if (hasGroup("Doctor")) {
    return AuthUserGroup.DOCTOR;
  }

  if (hasGroup("Historian")) {
    return AuthUserGroup.HISTORIAN;
  }

  if (hasGroup("MedicalAssistant")) {
    return AuthUserGroup.MEDICAL_ASSISTANT;
  }

  return AuthUserGroup.UNAUTHORIZED;
}

// Roles that get the gentle expired-session experience (stay on the current
// page + dismissable banner) instead of a hard redirect to /login, so nobody
// loses unsaved work mid-task. Any established staff session qualifies; a
// never-authenticated or UNAUTHORIZED user still redirects.
const isGraceEligibleRole = (group: AuthUserGroup | null): boolean =>
  group !== null && group !== AuthUserGroup.UNAUTHORIZED;

export const AuthSessionProvider = ({ children }: { children: ReactNode }) => {
  // Access auth user from Authenticator.Provider context
  const { user: authUser, signOut } = useAuthenticator((context) => [
    context.user,
    context.signOut,
  ]);
  // Single source of truth for auth session
  const [authSession, setAuthSession] = useState<AuthSession | null>(null);
  const sessionRef = useRef<AuthSession | null>(null);
  const [user, setUser] = useState<AuthUser | null>(authUser);
  const [userGroup, setUserGroup] = useState<AuthUserGroup | null>(null);
  // Mirror of userGroup readable from the (stale-closure) refresh callback and
  // Hub listeners, so we can tell an expiring MA apart from other roles.
  const userGroupRef = useRef<AuthUserGroup | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Ref mirror of sessionExpired for the visibility/health handlers, which are
  // registered once and would otherwise close over a stale value.
  const sessionExpiredRef = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [s3, setS3] = useState<AWS.S3 | null>(null);
  const [lambda, setLambda] = useState<AWS.Lambda | null>(null);
  const [cognitoIdp, setCognitoIdp] =
    useState<AWS.CognitoIdentityServiceProvider | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Promise deduplication - only one fetchAuthSession() call at a time
  const sessionPromiseRef = useRef<Promise<AuthSession> | null>(null);
  // Whether the in-flight refresh is a force refresh. A force caller can only
  // safely reuse an in-flight call if that call is itself a force refresh
  // (a non-force call may resolve to cached credentials).
  const sessionPromiseIsForceRef = useRef<boolean>(false);
  // Identity of the call that currently owns the in-flight slot, so a call's
  // cleanup only clears the slot when it still owns it (a force call may have
  // overtaken an earlier non-force call — see configureAuthSession).
  const activeRequestTokenRef = useRef<object | null>(null);
  // Epoch ms until which forced refreshes are suppressed after a throttle.
  const throttleCooldownUntilRef = useRef<number>(0);
  const amplifyConfig = Amplify.getConfig();
  const region =
    amplifyConfig.API?.GraphQL?.region || amplifyConfig.Storage?.S3?.region;

  const clearAuthSessionState = useCallback((authError?: Error) => {
    setAuthSession(null);
    sessionRef.current = null;
    setUser(null);
    setUserGroup(null);
    setS3(null);
    setLambda(null);
    setCognitoIdp(null);
    setError(authError ?? null);
    errorReporter.clearUserContext();
  }, []);

  // Enter expired-session grace mode. Deliberately does NOT clear the React auth
  // state (authSession/user/userGroup/clients): nulling it unmounts
  // auth-dependent subtrees and blanks the page, defeating the whole point of
  // keeping the user on their work. The banner + useSessionExpiryRedirect handle
  // the rest, and a later successful refresh (e.g. re-auth in another tab)
  // clears sessionExpired.
  //
  // Also does NOT clear the Cognito tokens. They live in localStorage shared by
  // every tab, so wiping them on one tab's failed refresh logs out all the
  // others — which turned a single stale tab into a repeat-logout loop
  // (portal/specs/session-expiry-grace-hardening.md). A genuinely rejected
  // refresh token is already cleared by Amplify itself
  // (TokenOrchestrator.handleErrors), so /login stays clean without us.
  const enterExpiredGrace = useCallback(() => {
    setSessionExpired(true);
    sessionExpiredRef.current = true;
    setSessionGraceActive(true);
  }, []);

  // Single method to fetch and configure everything
  const configureAuthSession = useCallback(
    async (forceRefresh = false): Promise<AuthSession | null> => {
      // Single-flight: coalesce concurrent calls onto one in-flight refresh so
      // a burst of force-refresh requests (e.g. many failing queries on the
      // appointment page all asking for fresh credentials at once) collapses
      // into a single Cognito call instead of a thundering herd that throttles
      // the Identity Pool. A force caller can reuse the in-flight call only when
      // that call is itself a force refresh; otherwise it may resolve to cached
      // credentials and the force caller would not get the refresh it needs.
      if (
        sessionPromiseRef.current &&
        (!forceRefresh || sessionPromiseIsForceRef.current)
      ) {
        try {
          return await sessionPromiseRef.current;
        } catch {
          // If the in-flight call failed, fall through to a new attempt.
        }
      }

      // Throttle circuit-breaker: once Cognito returns TooManyRequestsException,
      // stop hitting it until the cooldown elapses. Returning the last-known
      // session lets the app degrade gracefully instead of amplifying the
      // throttle into total auth loss / a white screen. Applies to force AND
      // non-force calls — a non-force call still reaches the identity-pool
      // credential leg on a token rotation (hasTokenChanged), which is exactly
      // what trips the limit — but only short-circuits when we actually have a
      // cached session to serve, so an initial (session-less) load still tries.
      if (Date.now() < throttleCooldownUntilRef.current && sessionRef.current) {
        errorReporter.addFeatureBreadcrumb(
          "auth-session",
          "throttle-cooldown",
          {
            forceRefresh,
            cooldownRemainingMs: throttleCooldownUntilRef.current - Date.now(),
          },
        );
        return sessionRef.current;
      }

      // Per-call identity captured before the IIFE so the finally can tell
      // whether this call still owns the in-flight slot.
      const requestToken = {};

      const promise = (async (): Promise<AuthSession> => {
        try {
          errorReporter.addFeatureBreadcrumb("auth-session", "started", {
            forceRefresh,
            region,
          });

          // Single session fetch, with a jittered retry on the identity-pool
          // throttle so a transient TooManyRequestsException from a burst rides
          // out instead of failing the whole configure.
          const session = await fetchAuthSessionWithRetry({ forceRefresh });

          // Fetch user from Amplify as fallback in case
          // authenticator user is not automatically fetched
          if (authUser) {
            setUser(authUser);
          } else {
            try {
              const currentUser = await getCurrentUser();
              setUser(currentUser);
            } catch (err) {
              errorReporter.reportApplicationError(toError(err), {
                tags: {
                  component: "AuthSessionProvider",
                  operation: "get_current_user",
                  service: "aws_auth",
                  errorCategory: "auth_error",
                },
              });
              console.warn("Failed to get current user:", err);
            }
          }

          // Extract user group from session
          const group = getAuthUserGroup(session);

          // Configure AWS SDK if we have credentials
          if (session.credentials) {
            AWS.config.update({
              region,
              credentials: session.credentials,
            });

            // Initialize AWS clients.
            // signatureVersion "v4" makes presigned URLs use AWS Signature V4
            // (X-Amz-* query params) instead of the legacy SigV2
            // (AWSAccessKeyId/Signature/Expires). SigV4 is AWS's recommended,
            // non-deprecated scheme and is required in all regions created
            // after 2014; SigV2 is only still honored in a few legacy regions.
            setS3(
              new AWS.S3({
                useAccelerateEndpoint: USE_TRANSFER_ACCELERATION,
                signatureVersion: "v4",
              }),
            );
            setLambda(new AWS.Lambda());
            setCognitoIdp(new AWS.CognitoIdentityServiceProvider());
          }

          // Set error reporting context
          if (session.tokens?.accessToken?.payload?.sub) {
            const userId = session.tokens.accessToken.payload.sub;
            errorReporter.setAnonymousUserContext(userId, group);
            // Role is the primary breakdown dimension for UX metrics.
            analytics.setUserRole(group);
          }

          // Update all state atomically
          setAuthSession(session);
          sessionRef.current = session;
          setUserGroup(group);
          userGroupRef.current = group;
          setError(null);
          // A successful (re)configure means creds are valid again — clear any
          // expired-session grace. This is how an MA recovers by signing in on
          // another tab: the visibility/health refresh picks up the fresh
          // tokens and the banner disappears.
          setSessionExpired(false);
          sessionExpiredRef.current = false;
          setSessionGraceActive(false);

          errorReporter.addFeatureBreadcrumb("auth-session", "completed", {
            userGroup: group,
            hasCredentials: !!session.credentials,
            sessionExpiration: session.credentials?.expiration?.toISOString(),
            hasS3: true,
            hasLambda: true,
          });

          return session;
        } catch (err) {
          const error = toError(err);

          // Cognito throttled the refresh — back off so we don't keep hitting
          // the rate limit and turn a transient throttle into sustained auth
          // loss. This is not an invalid session, so we do NOT clear/redirect.
          if (isThrottleError(error)) {
            throttleCooldownUntilRef.current =
              Date.now() + THROTTLE_COOLDOWN_MS;
            errorReporter.addFeatureBreadcrumb(
              "auth-session",
              "throttled-cooldown-set",
              { cooldownMs: THROTTLE_COOLDOWN_MS },
            );
          }

          const isInvalidSession = isInvalidAuthSessionError(error);
          // userGroupRef mirrors the last-known role; it survives across retries
          // (grace mode doesn't clear it), so this read is stable.
          const graceEligible = isGraceEligibleRole(userGroupRef.current);

          if (isInvalidSession) {
            if (graceEligible) {
              // Keep the user on their page with a banner instead of
              // hard-redirecting; useSessionExpiryRedirect sends them to /login
              // only once they navigate away.
              enterExpiredGrace();
            } else {
              clearAuthSessionState(error);
              clearCognitoLocalStorageKeys();
            }
            errorReporter.addFeatureBreadcrumb(
              "auth-session",
              graceEligible
                ? "invalid-session-grace"
                : "invalid-session-cleared",
              {
                forceRefresh,
                errorName: error.name,
              },
            );
          } else {
            setError(error);
          }

          errorReporter.reportApplicationError(error, {
            tags: {
              component: "AuthSessionProvider",
              operation: "configure_auth_session",
              service: "aws_auth",
              errorCategory: "auth_error",
            },
            extra: {
              forceRefresh,
              region,
              hasAuthUser: !!authUser,
              errorName: error.name,
              errorMessage: error.message,
            },
          });

          console.error("Error configuring auth session:", error);
          if (isInvalidSession && !graceEligible) {
            window.location.replace(getLoginRedirectUrl());
          }
          throw error;
        } finally {
          setIsLoading(false);
          // Only clear the in-flight slot if this call still owns it. A force
          // caller is allowed to overtake an in-flight non-force call (see the
          // single-flight guard above), so a fast non-force promise can finish
          // after a force promise has already claimed the slot — clearing
          // unconditionally would orphan the force refresh from the dedup and
          // let the herd re-form.
          if (activeRequestTokenRef.current === requestToken) {
            sessionPromiseRef.current = null;
            sessionPromiseIsForceRef.current = false;
            activeRequestTokenRef.current = null;
          }
        }
      })();

      sessionPromiseRef.current = promise;
      sessionPromiseIsForceRef.current = forceRefresh;
      activeRequestTokenRef.current = requestToken;
      try {
        return await promise;
      } catch {
        return null;
      }
    },
    [region, authUser, clearAuthSessionState, enterExpiredGrace],
  );

  // Initialize on mount and handle auth events
  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    void configureAuthSession();

    // Listen to all auth events in one place
    const unsubscribeAuth = Hub.listen(
      "auth",
      async (data: { payload: { event: string } }) => {
        const { event } = data.payload;

        console.log("AuthSessionProvider: auth event", event);

        switch (event) {
          case "signedIn":
          case "tokenRefresh":
            // Token was refreshed automatically by Amplify
            errorReporter.addFeatureBreadcrumb(
              "token-refresh",
              "amplify-triggered",
              { event },
            );
            await configureAuthSession(false);
            break;

          case "tokenRefresh_failure":
            // Token refresh failed - force refresh
            errorReporter.addFeatureBreadcrumb(
              "token-refresh",
              "amplify-failed",
              {
                event,
              },
            );
            await configureAuthSession(true);
            break;

          case "signedOut":
            // Clear everything
            clearAuthSessionState();
            errorReporter.addFeatureBreadcrumb("auth-session", "cleared", {
              event,
            });
            // Redirect to login page after sign out is complete
            window.location.href = "/login";
            break;
        }
      },
    );

    // Custom listener for manual token refresh requests
    const unsubscribeApplication = Hub.listen(
      "application",
      async (data: { payload: { event: string } }) => {
        if (data.payload.event === STALE_IDENTITY_RECOVERED_EVENT) {
          // singleFlightCredentialsProvider healed a tab holding another
          // user's identity id. Reported so we can see how often it happens.
          errorReporter.reportMessage(
            "Recovered a stale Identity-Pool identity id",
            {
              level: "warning",
              tags: {
                component: "AuthSessionProvider",
                operation: "stale-identity-recovery",
                service: "aws_auth",
              },
            },
          );
          return;
        }
        if (data.payload.event === "forceTokenRefresh_request") {
          errorReporter.addFeatureBreadcrumb(
            "token-refresh",
            "manual-request-triggered",
          );
          const session = await configureAuthSession(true);
          if (session) {
            Hub.dispatch("application", {
              event: "forceTokenRefresh_success",
            });
          } else {
            Hub.dispatch("application", {
              event: "forceTokenRefresh_failure",
            });
          }
        }
      },
    );

    // Fallback health check - runs every 10 minutes to ensure AWS services are still configured
    const healthCheckInterval = setInterval(async () => {
      try {
        const credentials = sessionRef.current?.credentials;
        const expiration = credentials?.expiration?.getTime();
        const currentTime = Date.now();

        // Refresh if the token is within the interval + a buffer, or whenever
        // we're in expired-session grace — the stale creds still look valid, so
        // this is how we detect a recovery (e.g. re-auth in another tab).
        if (
          !expiration ||
          expiration - currentTime < HEALTH_CHECK_BUFFER_MS ||
          sessionExpiredRef.current
        ) {
          errorReporter.addFeatureBreadcrumb(
            "token-refresh",
            "health-check-triggered",
            {
              timeUntilExpiration: expiration ? expiration - currentTime : null,
            },
          );

          // Jitter so simultaneous multi-tab wake-ups don't burst together.
          await new Promise((resolve) =>
            setTimeout(resolve, Math.random() * HEALTH_CHECK_JITTER_MS),
          );
          await configureAuthSession(true);
        }
      } catch (error) {
        const errorObj = toError(error, "Health check failed");
        console.error(errorObj);
        errorReporter.reportApplicationError(errorObj, {
          level: "info",
          tags: {
            component: "AuthSessionProvider",
            operation: "health-check",
            service: "aws_auth",
          },
        });
      }
    }, HEALTH_CHECK_INTERVAL_MS);

    // Proactive refresh on tab re-focus. The 10-minute health-check setInterval
    // is paused while the laptop sleeps, so on wake the tokens may already be
    // expired and every query refetches at once (react-query refetchOnWindowFocus
    // is on) — the NoSignedUser stampede. Refresh first (deduped via
    // configureAuthSession's single-flight) so those refetches use fresh creds.
    const handleVisibilityRefresh = async () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      try {
        const expiration =
          sessionRef.current?.credentials?.expiration?.getTime();
        const currentTime = Date.now();

        // Refresh if creds are missing or within the expiry buffer (avoids
        // refreshing on every routine tab switch), or whenever we're in
        // expired-session grace so a re-auth in another tab is picked up.
        if (
          !expiration ||
          expiration - currentTime < HEALTH_CHECK_BUFFER_MS ||
          sessionExpiredRef.current
        ) {
          errorReporter.addFeatureBreadcrumb(
            "token-refresh",
            "visibility-triggered",
            {
              timeUntilExpiration: expiration ? expiration - currentTime : null,
            },
          );
          await configureAuthSession(true);
        }
      } catch (error) {
        const errorObj = toError(error, "Visibility refresh failed");
        console.error(errorObj);
        errorReporter.reportApplicationError(errorObj, {
          level: "info",
          tags: {
            component: "AuthSessionProvider",
            operation: "visibility-refresh",
            service: "aws_auth",
          },
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityRefresh);

    return () => {
      unsubscribeAuth();
      unsubscribeApplication();
      clearInterval(healthCheckInterval);
      document.removeEventListener("visibilitychange", handleVisibilityRefresh);
    };
  }, [configureAuthSession]);

  // Defense-in-depth: detect inconsistent impersonation state.
  // If the JWT has impersonation claims but the atom says we're not impersonating,
  // this is an inconsistent state (e.g., localStorage was manually cleared).
  // Only restore admin session if backup exists — never call signOut() as it clears
  // shared localStorage tokens and would break impersonation in all tabs.
  useEffect(() => {
    if (!authSession?.tokens?.idToken) return;
    const impersonatorSub = authSession.tokens.idToken.payload?.[
      "custom:impersonator_sub"
    ] as string | undefined;
    const impersonationState = getDefaultStore().get(impersonationAtom);

    if (impersonatorSub && !impersonationState.isActive) {
      if (hasAdminSessionBackup()) {
        console.warn(
          "Impersonation JWT detected but no active impersonation state — restoring admin session",
        );
        restoreAdminSession();
        window.location.href = "/";
      }
    }
  }, [authSession]);

  // If a prior assume attempt aborted and restored the admin (see
  // completePendingImpersonation), tell them why they're back in their own
  // account instead of leaving it looking like an unexplained logout.
  //
  // Gate on a confirmed live session (idToken present) rather than firing on
  // bare mount: consuming immediately would clear the one-shot flag before
  // <Toaster/> mounts, and if the just-restored admin token is itself invalid
  // (configureAuthSession then hard-redirects to /login), the message would be
  // lost with no way to show it again. Waiting for a live session means an
  // immediate redirect leaves the flag intact; its TTL bounds staleness.
  useEffect(() => {
    if (!authSession?.tokens?.idToken) return;
    const errorMessage = consumeImpersonationError();
    if (errorMessage)
      toast({ message: errorMessage, variant: "error", duration: 8000 });
  }, [authSession]);

  // Dev-only helper to exercise the expired-session flow without waiting for a
  // real Cognito token to expire. Exposed as window.__simulateSessionExpiry()
  // on the dev environment only (getCurrentEnv gates it out of stage/prod).
  // Mirrors configureAuthSession's real invalid-session branch: eligible staff
  // sessions enter grace mode (stay on page + banner); anything else redirects.
  useEffect(() => {
    if (getCurrentEnv() !== "dev") return;
    const win = window as typeof window & {
      __simulateSessionExpiry?: () => void;
      __simulateImpersonationFailure?: () => void;
    };
    win.__simulateSessionExpiry = () => {
      const graceEligible = isGraceEligibleRole(userGroupRef.current);
      if (graceEligible) {
        // A real expiry has Amplify clear the rejected tokens; mimic that so
        // the next refresh doesn't simply succeed and drop the banner.
        clearCognitoLocalStorageKeys();
        enterExpiredGrace();
        console.warn("[dev] Simulated session expiry — grace mode active");
      } else {
        clearAuthSessionState();
        clearCognitoLocalStorageKeys();
        console.warn(
          "[dev] Simulated session expiry — redirecting (not grace-eligible)",
        );
        window.location.replace(getLoginRedirectUrl());
      }
    };
    // Arm a forced abort of the next assume completion to exercise the
    // impersonation recovery (restore admin + error toast + redirect back).
    win.__simulateImpersonationFailure = () => {
      armImpersonationFailureForTest();
      console.warn(
        "[dev] Armed: the next 'Assume User' attempt will abort at completion. Click Assume now.",
      );
    };
    return () => {
      win.__simulateSessionExpiry = undefined;
      win.__simulateImpersonationFailure = undefined;
    };
  }, [clearAuthSessionState, enterExpiredGrace]);

  if (isLoading && !authSession) {
    return null;
  }

  return (
    <AuthSessionContext.Provider
      value={{
        authSession,
        userGroup,
        isLoading,
        s3,
        lambda,
        cognitoIdp,
        authUser: user,
        signOut,
        sessionExpired,
        error,
      }}
    >
      {children}
    </AuthSessionContext.Provider>
  );
};

// Single hook for all auth needs
export const useAuthSession = (): AuthSessionContextType => {
  const context = useContext(AuthSessionContext);
  if (!context) {
    const error = new Error(
      "useAuthSession must be used within AuthSessionProvider",
    );

    // Report context usage errors
    errorReporter.reportApplicationError(error, {
      tags: {
        component: "AuthSessionProvider",
        operation: "use_auth_session_hook",
        errorType: "context_usage_error",
        errorCategory: "react_error",
      },
      extra: {
        hookName: "useAuthSession",
        contextAvailable: !!context,
      },
    });

    throw error;
  }
  return context;
};
